import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Calendar, X, MoreVertical, Check, Square, CheckSquare } from 'lucide-react';
import { KanbanBoard, BoardColumnData } from './KanbanBoard';
import { useKanban, KanbanCard, KanbanColumn, KanbanLabel, ChecklistItem } from '../../hooks/useKanban';
import { useApi } from '../../hooks/useApi';
import type { Priority } from '../../hooks/useReminders';

const PRIORITY_STYLES: Record<string, string> = {
  alta: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  media: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  bassa: 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-slate-300',
};

const LABEL_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'];

interface ClientOption { id: string; company_name: string; logo_url: string | null }

function toInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fromInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
function formatDue(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}
function isOverdue(iso: string | null): boolean {
  return !!iso && new Date(iso).getTime() < Date.now();
}

// ---- Card in colonna ----
function CardView({ card, onClick }: { card: KanbanCard; onClick: () => void }) {
  const total = card.checklist?.length || 0;
  const done = card.checklist?.filter((c) => c.done).length || 0;
  const remaining = (card.checklist || []).filter((c) => !c.done);
  const overdue = isOverdue(card.due_date) && !card.completed;

  return (
    <button
      onClick={onClick}
      className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
    >
      {/* Cliente */}
      {(card.client_logo || card.client_name) && (
        <div className="mb-2 flex items-center gap-2">
          {card.client_logo ? (
            <img src={card.client_logo} alt="" className="h-6 w-6 flex-shrink-0 rounded-md object-cover" />
          ) : (
            <div className="h-6 w-6 flex-shrink-0 rounded-md bg-slate-200 dark:bg-gray-700" />
          )}
          <span className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">{card.client_name}</span>
        </div>
      )}

      {/* Etichette */}
      {card.labels?.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {card.labels.map((l, i) => (
            <span key={i} className="rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ backgroundColor: l.color }}>
              {l.name}
            </span>
          ))}
        </div>
      )}

      {/* Titolo */}
      <div className={`text-sm font-semibold ${card.completed ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100'}`}>
        {card.title}
      </div>

      {/* Nota (stato/blocco) — anteprima */}
      {card.description && (
        <p className="mt-1 line-clamp-2 whitespace-pre-line text-xs text-slate-500 dark:text-slate-400">{card.description}</p>
      )}

      {/* Checklist: cosa manca da fare */}
      {total > 0 && (
        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Da fare</span>
            <span className="text-[11px] text-slate-400">{done}/{total}</span>
          </div>
          <div className="mb-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </div>
          <ul className="space-y-1">
            {remaining.slice(0, 6).map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                <Square className="mt-0.5 h-3 w-3 flex-shrink-0 text-slate-400" />
                <span className="leading-snug">{c.text}</span>
              </li>
            ))}
            {remaining.length > 6 && <li className="pl-4 text-[11px] text-slate-400">+{remaining.length - 6} altre…</li>}
            {remaining.length === 0 && (
              <li className="flex items-center gap-1.5 text-xs text-emerald-600">
                <Check className="h-3.5 w-3.5" /> Tutto fatto
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Footer: priorità + scadenza */}
      {(card.priority || card.due_date) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {card.priority && (
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${PRIORITY_STYLES[card.priority]}`}>{card.priority}</span>
          )}
          {card.due_date && (
            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${overdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400'}`}>
              <Calendar className="h-3 w-3" />
              {formatDue(card.due_date)}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

interface CardForm {
  id?: string;
  columnId: string;
  title: string;
  checklist: ChecklistItem[];
  due_date: string;
  priority: Priority;
  labels: KanbanLabel[];
  client_id: string | null;
  completed: boolean;
  description: string;
}

function CardModal({
  initial,
  clients,
  onClose,
  onSave,
  onDelete,
}: {
  initial: CardForm;
  clients: ClientOption[];
  onClose: () => void;
  onSave: (f: CardForm) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [form, setForm] = useState<CardForm>(initial);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState(LABEL_COLORS[0]);
  const isEdit = !!initial.id;
  const client = clients.find((c) => c.id === form.client_id);

  const setChecklist = (checklist: ChecklistItem[]) => setForm({ ...form, checklist });
  const addItem = () => {
    if (!newItem.trim()) return;
    setChecklist([...form.checklist, { text: newItem.trim(), done: false }]);
    setNewItem('');
  };
  const addLabel = () => {
    if (!labelName.trim()) return;
    setForm({ ...form, labels: [...form.labels, { name: labelName.trim(), color: labelColor }] });
    setLabelName('');
  };
  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try { await onSave(form); onClose(); } catch { /* toast nel hook */ } finally { setSaving(false); }
  };

  const done = form.checklist.filter((c) => c.done).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-8" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5 dark:border-gray-800">
          <div className="flex-1">
            {client && (
              <div className="mb-2 flex items-center gap-2">
                {client.logo_url && <img src={client.logo_url} alt="" className="h-7 w-7 rounded-md object-cover" />}
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{client.company_name}</span>
              </div>
            )}
            <input
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Titolo progetto"
              className="w-full bg-transparent text-lg font-semibold text-slate-900 focus:outline-none dark:text-slate-100"
            />
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[65vh] space-y-5 overflow-y-auto p-5">
          {/* Cliente / priorità / scadenza */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Cliente</label>
              <select
                value={form.client_id || ''}
                onChange={(e) => setForm({ ...form, client_id: e.target.value || null })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              >
                <option value="">Nessun cliente</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Priorità</label>
              <select
                value={form.priority || ''}
                onChange={(e) => setForm({ ...form, priority: (e.target.value || null) as Priority })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              >
                <option value="">Nessuna</option>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="bassa">Bassa</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Scadenza</label>
              <input
                type="date"
                value={toInput(form.due_date || null)}
                onChange={(e) => setForm({ ...form, due_date: fromInput(e.target.value) || '' })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              />
            </div>
          </div>

          {/* CHECKLIST — cosa manca da fare */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">Cosa manca da fare</label>
              {form.checklist.length > 0 && <span className="text-xs text-slate-400">{done}/{form.checklist.length}</span>}
            </div>
            <div className="space-y-1.5">
              {form.checklist.map((item, i) => (
                <div key={i} className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-gray-800">
                  <button
                    onClick={() => setChecklist(form.checklist.map((c, j) => (j === i ? { ...c, done: !c.done } : c)))}
                    className="flex-shrink-0"
                  >
                    {item.done ? <CheckSquare className="h-4 w-4 text-emerald-500" /> : <Square className="h-4 w-4 text-slate-400" />}
                  </button>
                  <input
                    value={item.text}
                    onChange={(e) => setChecklist(form.checklist.map((c, j) => (j === i ? { ...c, text: e.target.value } : c)))}
                    className={`flex-1 bg-transparent text-sm focus:outline-none ${item.done ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-200'}`}
                  />
                  <button
                    onClick={() => setChecklist(form.checklist.filter((_, j) => j !== i))}
                    className="flex-shrink-0 text-slate-300 opacity-0 hover:text-red-500 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Plus className="h-4 w-4 text-slate-400" />
              <input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addItem()}
                placeholder="Aggiungi un'attività e premi Invio"
                className="flex-1 border-b border-transparent bg-transparent py-1 text-sm text-slate-700 focus:border-slate-300 focus:outline-none dark:text-slate-200"
              />
            </div>
          </div>

          {/* Note (stato, blocco, criterio) */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200">Note</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              placeholder="Stato, blocco principale, criterio di completamento…"
              className="w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 focus:border-slate-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200"
            />
          </div>

          {/* Etichette */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">Etichette</label>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {form.labels.map((l, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-white" style={{ backgroundColor: l.color }}>
                  {l.name}
                  <button onClick={() => setForm({ ...form, labels: form.labels.filter((_, j) => j !== i) })}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <div className="flex gap-1">
                {LABEL_COLORS.map((c) => (
                  <button key={c} onClick={() => setLabelColor(c)} className={`h-5 w-5 rounded-full ${labelColor === c ? 'ring-2 ring-slate-400 ring-offset-1' : ''}`} style={{ backgroundColor: c }} />
                ))}
              </div>
              <input
                value={labelName}
                onChange={(e) => setLabelName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addLabel()}
                placeholder="nome etichetta"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              />
              <button onClick={addLabel} className="rounded-lg bg-slate-200 px-2 py-1 text-xs dark:bg-gray-700">+</button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={form.completed} onChange={(e) => setForm({ ...form, completed: e.target.checked })} />
            Progetto completato
          </label>
        </div>

        {/* Footer azioni */}
        <div className="flex items-center justify-between border-t border-slate-100 p-4 dark:border-gray-800">
          {isEdit && onDelete ? (
            <button onClick={async () => { await onDelete(); onClose(); }} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="h-4 w-4" /> Elimina
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800">Annulla</button>
            <button onClick={save} disabled={saving || !form.title.trim()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-gray-900">
              {saving ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColumnHeader({ col, onRename, onDelete }: { col: KanbanColumn; onRename: (t: string) => void; onDelete: () => void }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(col.title);
  const [menu, setMenu] = useState(false);
  return (
    <div className="flex w-full items-center justify-between">
      {editing ? (
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { setEditing(false); if (title.trim() && title !== col.title) onRename(title.trim()); }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          className="w-full rounded border border-slate-300 bg-white px-1.5 py-0.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-slate-100"
        />
      ) : (
        <div className="flex items-center gap-2">
          <span className="cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-200" onClick={() => setEditing(true)}>{col.title}</span>
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-600 dark:bg-gray-700 dark:text-slate-300">{col.cards.length}</span>
        </div>
      )}
      <div className="relative">
        <button onClick={() => setMenu(!menu)} className="rounded p-1 text-slate-400 hover:bg-slate-200 dark:hover:bg-gray-700"><MoreVertical className="h-4 w-4" /></button>
        {menu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
            <div className="absolute right-0 z-20 mt-1 w-36 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
              <button onClick={() => { setEditing(true); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-gray-700">Rinomina</button>
              <button onClick={() => { if (confirm(`Eliminare la colonna "${col.title}" e le sue card?`)) onDelete(); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">Elimina colonna</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const ProgressiSitiBoard: React.FC = () => {
  const { columns, loading, error, addColumn, renameColumn, deleteColumn, addCard, updateCard, deleteCard, moveCard } = useKanban('progressi_siti');
  const api = useApi();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [modal, setModal] = useState<CardForm | null>(null);
  const [newColumn, setNewColumn] = useState('');
  const [addingColumn, setAddingColumn] = useState(false);

  useEffect(() => {
    api('/clients?limit=500')
      .then((data) => setClients((Array.isArray(data) ? data : []).map((c: any) => ({ id: c.id, company_name: c.company_name, logo_url: c.logo_url || null }))))
      .catch(() => setClients([]));
  }, [api]);

  const boardColumns: BoardColumnData<KanbanCard>[] = useMemo(
    () => columns.map((c) => ({ id: c.id, title: c.title, cards: c.cards })),
    [columns]
  );

  const emptyForm = (columnId: string): CardForm => ({
    columnId, title: '', checklist: [], due_date: '', priority: null, labels: [], client_id: null, completed: false, description: '',
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400">Stato di avanzamento dei progetti in corso.</p>
        {addingColumn ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={newColumn}
              onChange={(e) => setNewColumn(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newColumn.trim()) { addColumn(newColumn.trim()); setNewColumn(''); setAddingColumn(false); }
                if (e.key === 'Escape') setAddingColumn(false);
              }}
              placeholder="Nome colonna"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
            />
            <button onClick={() => { if (newColumn.trim()) addColumn(newColumn.trim()); setNewColumn(''); setAddingColumn(false); }} className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white dark:bg-white dark:text-gray-900">Aggiungi</button>
          </div>
        ) : (
          <button onClick={() => setAddingColumn(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800">
            <Plus className="h-4 w-4" /> Colonna
          </button>
        )}
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div>}

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Caricamento…</div>
      ) : (
        <KanbanBoard
          columns={boardColumns}
          getCardId={(c: KanbanCard) => c.id}
          onCardMove={(id, toCol, toIdx) => moveCard(id, toCol, toIdx)}
          renderColumnHeader={(col) => {
            const full = columns.find((c) => c.id === col.id)!;
            return <ColumnHeader col={full} onRename={(t) => renameColumn(col.id, t)} onDelete={() => deleteColumn(col.id)} />;
          }}
          renderColumnFooter={(col) => (
            <button onClick={() => setModal(emptyForm(col.id))} className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-slate-500 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-gray-700/50">
              <Plus className="h-4 w-4" /> Aggiungi card
            </button>
          )}
          renderCard={(card: KanbanCard) => (
            <CardView
              card={card}
              onClick={() =>
                setModal({
                  id: card.id,
                  columnId: card.column_id,
                  title: card.title,
                  checklist: card.checklist || [],
                  due_date: card.due_date || '',
                  priority: card.priority,
                  labels: card.labels || [],
                  client_id: card.client_id,
                  completed: card.completed,
                  description: card.description || '',
                })
              }
            />
          )}
        />
      )}

      {modal && (
        <CardModal
          initial={modal}
          clients={clients}
          onClose={() => setModal(null)}
          onSave={async (f) => {
            const payload = {
              title: f.title,
              description: f.description || null,
              checklist: f.checklist,
              due_date: f.due_date || null,
              priority: f.priority,
              labels: f.labels,
              client_id: f.client_id,
              completed: f.completed,
            };
            if (f.id) await updateCard(f.id, payload as any);
            else await addCard(f.columnId, payload as any);
          }}
          onDelete={modal.id ? async () => deleteCard(modal.id!) : undefined}
        />
      )}
    </div>
  );
};

export default ProgressiSitiBoard;
